import { GraphQLClient, ClientError } from "graphql-request";
import type { Auth } from "firebase/auth";

export default defineNuxtPlugin({
  name: "api",
  setup(nuxtApp) {
    const runtimeConfig = useRuntimeConfig();
    const endpoint = runtimeConfig.public.endpoint;
    const gatewayApiKey = runtimeConfig.public.gatewayApiKey;

  const getAuthToken = async (forceRefresh = false): Promise<string | null> => {
    const idTokenCookie = useCookie("id_token");
    const auth = nuxtApp.$auth as Auth;

    if (auth.currentUser) {
      const freshToken = await auth.currentUser.getIdToken(forceRefresh);
      if (freshToken) {
        idTokenCookie.value = freshToken;
        useAuthStore().setIdToken(freshToken);
        return freshToken;
      }
    }

    return idTokenCookie.value ?? null;
  };

  const buildHeaders = (token: string | null) => {
    const headers: Record<string, string> = {};

    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    if (gatewayApiKey) {
      headers["x-api-key"] = gatewayApiKey;
    }

    return headers;
  };

  /**
   * Make a GraphQL request
   *
   * @param {string} query The query to make
   * @param {object} variables The variables to pass to the query
   * @returns {Promise<object>} The response from the query
   */
  const gqlRequest = async (query: string, variables: object = {}): Promise<object> => {
    const token = await getAuthToken();
    
    let client = new GraphQLClient(endpoint, {
      headers: buildHeaders(token),
    });

    try {
      // Attempt the request
      return await client.request(query, variables);
    } catch (error) {
      if (error instanceof ClientError && error.response.status === 401) {
        const newToken = await getAuthToken(true);
        if (!newToken) {
          throw error;
        }

        // Retry the request with the new token
        client = new GraphQLClient(endpoint, {
          headers: buildHeaders(newToken),
        });

        return await client.request(query, variables);
      } else {
        throw error;
      }
    }
  };

  /**
   * Make an authenticated fetch request
   * @param {string} url The URL to fetch
   * @param {string} method The method to use
   * @param {object} body The body to send
   */
  const authdFetch = async (
    url: string,
    method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
    body?: object,
    hasFiles = false
  ) => {
    const token = await getAuthToken();
    const myHeaders = new Headers();
    
    if (!hasFiles) {
      myHeaders.append("Content-Type", "application/json");
    }
    if (token) {
      myHeaders.append("Authorization", `Bearer ${token}`);
    }
    if (gatewayApiKey) {
      myHeaders.append("x-api-key", gatewayApiKey);
    }

    const bodyData = !hasFiles ? JSON.stringify(body) : body;

    const options = {
      method,
      headers: myHeaders,
      ...(body && { body: bodyData }),
    };

    try {
      const response = await $fetch(url, options);
      return response;
    } catch (error: unknown) {
      if (
        error instanceof Error &&
        error.name === "FetchError" &&
        [400, 401].includes((error as { response?: { status?: number } }).response?.status ?? 0)
      ) {
        const newToken = await getAuthToken(true);
        if (!newToken) {
          throw error;
        }

        // Retry the request with the new token
        myHeaders.set("Authorization", `Bearer ${newToken}`);
        const retryOptions = {
          method,
          headers: myHeaders,
          ...(body && { body: bodyData }),
        };
        return await $fetch(url, retryOptions);
      }
      throw error;
    }
  };

    nuxtApp.provide("gqlRequest", gqlRequest);
    nuxtApp.provide("authdFetch", authdFetch);
  },
});